import { t } from '@app/i18n';
import { ConfirmDialog, ErrorBanner } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import type { User } from './UserModel';

export type KindConfirmacao = 'desativar' | 'reativar' | 'senha';

export interface UserConfirmacao {
  tipo: KindConfirmacao;
  usuario: User;
}

export interface UserConfirmProps {
  confirmacao: UserConfirmacao | null;
  pending: boolean;
  erro: ApiError | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * As três confirmações de T22 (FR-107, FR-130): desativar, reativar e redefinir
 * senha. Cada uma diz o efeito concreto antes de acontecer — desativar derruba
 * as sessões na hora (AC-06) e redefinir mostra a senha uma única vez (R20).
 *
 * O 409 `ultimo_admin` (FR-108) aparece dentro do próprio diálogo: é ali que a
 * pessoa acabou de pedir a ação.
 */
export function UserConfirm({ confirmacao, pending, erro, onConfirm, onCancel }: UserConfirmProps) {
  if (!confirmacao) return null;
  const { tipo, usuario } = confirmacao;

  const texts = {
    desativar: {
      title: t('users.confirm.deactivate.title', { name: usuario.name }),
      message: t('users.confirm.deactivate.message', { email: usuario.email }),
      confirmLabel: t('users.confirm.deactivate.action'),
      destructive: true,
    },
    reativar: {
      title: t('users.confirm.reactivate.title', { name: usuario.name }),
      // `usuario.role` é o valor do contrato, não rótulo de tela.
      message: t('users.confirm.reactivate.message', {
        email: usuario.email,
        role: usuario.role,
      }),
      confirmLabel: t('users.confirm.reactivate.action'),
      destructive: false,
    },
    senha: {
      title: t('users.confirm.password.title', { name: usuario.name }),
      message: t('users.confirm.password.message', { email: usuario.email }),
      confirmLabel: t('users.confirm.password.action'),
      destructive: false,
    },
  }[tipo];

  return (
    <ConfirmDialog
      open
      title={texts.title}
      message={texts.message}
      confirmLabel={texts.confirmLabel}
      destructive={texts.destructive}
      loading={pending}
      onConfirm={onConfirm}
      onCancel={onCancel}
      error={
        erro ? <ErrorBanner error={erro} escrita title={t('users.confirm.error')} hideLink /> : null
      }
    />
  );
}
